function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll(
      "a[href], button:not([disabled]), input:not([type=hidden]):enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex^='-'])"
    )
  ).filter((el) => el.offsetParent !== null);
}

function getSlides(slider) {
  if (Array.isArray(slider.slides) && slider.slides.length) {
    return slider.slides;
  }
  return Array.from(
    slider.container.querySelectorAll(":scope > .keen-slider__slide")
  );
}

/**
 * Keyboard support for keen-slider carousels used as product/flavour rows.
 * - Tab / Shift+Tab can leave the carousel after the last / before the first focusable.
 * - Optional data-collection behavior: advance to next slide only from "add to cart" on multi-card sliders.
 */
export function KeenSliderKeyboardNavigation(slider) {
  function onSlideKeydown(e) {
    if (e.key !== "Tab") return;

    const slides = getSlides(slider);
    if (!slides.length) return;

    const rel = slider.track.details.rel;
    const currentSlide = slides[rel];
    if (!currentSlide) return;

    const focusables = getFocusableElements(currentSlide);
    const active = document.activeElement;
    const fi = focusables.indexOf(active);
    if (fi === -1) return;

    const { shiftKey } = e;
    const newSlider = slider.container.getAttribute("data-collection");
    const lastButtonInsideCard = active.dataset?.action === "add-variant";

    // Forward: leave carousel after last focusable on last slide (no loop)
    if (
      !shiftKey &&
      rel === slides.length - 1 &&
      fi === focusables.length - 1
    ) {
      return;
    }

    // Backward: leave carousel before first focusable on first slide
    if (shiftKey && rel === 0 && fi === 0) {
      return;
    }

    // Shift+Tab: move to previous slide when focus is on first focusable (slide > 0)
    if (shiftKey && rel > 0 && fi === 0) {
      if (newSlider && !active.hasAttribute("href")) return;
      e.preventDefault();
      slider.prev();
      requestAnimationFrame(() => {
        const idx = slider.track.details.rel;
        const prevFocusables = getFocusableElements(slides[idx]);
        prevFocusables[prevFocusables.length - 1]?.focus();
      });
      return;
    }

    // Tab: move to next slide when focus is on last focusable (not on last slide)
    if (
      !shiftKey &&
      rel < slides.length - 1 &&
      fi === focusables.length - 1
    ) {
      if (newSlider && !lastButtonInsideCard) return;
      e.preventDefault();
      slider.next();
      requestAnimationFrame(() => {
        const idx = slider.track.details.rel;
        const nextFocusables = getFocusableElements(slides[idx]);
        nextFocusables[0]?.focus();
      });
    }
  }

  /**
   * When focus moves to a slide (Tab through tiles, or click), align the track so that
   * slide is active/centered. DOM tab order can move focus to a neighboring slide without
   * updating the slider's relative index — moveToIdx fixes that.
   */
  function onFocusIn(e) {
    const target = e.target;
    if (!target || !slider.container.contains(target)) return;

    const slide = target.closest(".keen-slider__slide");
    if (!slide || !slider.container.contains(slide)) return;

    const slidesNow = getSlides(slider);
    const idx = slidesNow.indexOf(slide);
    if (idx === -1) return;

    if (idx === slider.track.details.rel) return;

    slider.moveToIdx(idx, false, {
      duration: 320,
    });
  }

  slider.on("created", () => {
    const slides = getSlides(slider);
    slides.forEach((slide) => {
      slide.addEventListener("keydown", onSlideKeydown);
    });
    slider.container.addEventListener("focusin", onFocusIn);
  });
}
